import React, { useState } from 'react';
import classNames from 'classnames';
import {
	Card,
	ConfigProvider,
	Empty,
	Grid,
	Space,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/pro-solid-svg-icons';
import { faRightLeft } from '@fortawesome/pro-light-svg-icons';
import { CargoContractSides, EstimateStatus, EstimatorModeTypes } from '@shared/utils/constants';
import { standardSort } from '@shared/utils/standardSort';
import Button from '@client/components/Button';
import Table from '@client/components/Table/Table';
import CargoDetailsModal from '@client/screens/cargos/components/CargoDetailsModal/CargoDetailsModal';
import manOnBoat from '@client/assets/images/man_on_boat.svg';
import IntakeCalculatorModal from '@client/screens/cargos/components/IntakeCalculatorModal/IntakeCalculatorModal';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { getUserData } from '@client/lib/api';
import { getCargoCardColumns } from '../helpers/cargoCardColumns';
import { useCargoProvider } from '../context/CargoContext';
import { useCentralStore } from '../context/CentralStoreContext';
import styles from './styles/CargosCard.module.css';
import ImportFromCargoBookModal from './ImportFromCargoBookModal';

type CargosCardProps = {
	mode: EstimatorModeTypes;
};

const CargosCard = ({
	mode
}: CargosCardProps) => {
	const {
		estimate,
		cargos,
		onUpdateCargo,
		onCreateNewCargoMutator,
		onCreateNewCargoReletMutator,
		onDeleteCargoMutator,
		onDeleteCargoReletMutator,
		chartererOptions,
		portOptions,
		refreshEverything,
		setReletViewByEstimateId,
	} = useCargoProvider();

	const {
		selectedEstimateId,
	} = useCentralStore();

	const [userData] = useFetchedState(getUserData);

	const [showModal, setShowModal] = useState(false);
	const [editingCargoId, setEditingCargoId] = useState<number | null>(null);
	const [editingCargoIntakeId, setEditingCargoIntakeId] = useState<number | null>(null);
	const [pendingLoadingPortSelections, setPendingLoadingPortSelections] =
		useState<Record<number, number[]>>({});
	const [pendingDischargingPortSelections, setDischargingPortPendingSelections] =
		useState<Record<number, number[]>>({});
	const screens = Grid.useBreakpoint();
	const { componentDisabled } = ConfigProvider.useConfig();

	if (estimate == null) {
		return null;
	}

	const isFixed = estimate?.status === EstimateStatus.Fixed;

	const getRelevantCargoTypes = () => {
		if (userData == null) {
			return [];
		}

		const sort = (types: typeof userData.cargoTypes) => (
			types.sort((a, b) => a.name.localeCompare(b.name))
		);

		if (estimate?.vessel?.type == null) {
			return sort(userData.cargoTypes.filter((ct) => ct.active));
		}

		return sort(userData.cargoTypes.filter((ct) => (
			estimate?.vessel?.type != null && ct.vesselTypes?.includes(estimate?.vessel?.type) && ct.active
		)));
	};

	const hasCargoRelet = cargos.some(
		(cargo) => cargo.contractSide === CargoContractSides.VC_IN
	);

	const showReletCargoButton =
		mode === EstimatorModeTypes.NORMAL &&
		estimate.status !== EstimateStatus.Fixed &&
		!hasCargoRelet;

	const isReletCargoButtonDisabled = cargos.length !== 1 || isFixed;

	const sortedCargos = [...cargos].sort(standardSort('id'));

	const relevantCargoTypes = getRelevantCargoTypes();

	const cargoCardColumns = getCargoCardColumns({
		onDeleteCargoMutator,
		onDeleteCargoReletMutator,
		onUpdateCargo,
		setEditingCargoId,
		ports: portOptions,
		isFreightLocked: estimate.isTceLocked || estimate.isNetResultLocked,
		xxl: screens.xxl,
		chartererOptions,
		disabled: isFixed || componentDisabled,
		pendingLoadingPortSelections,
		setPendingLoadingPortSelections,
		pendingDischargingPortSelections,
		setDischargingPortPendingSelections,
		cargoTypes: relevantCargoTypes,
		fixingCompanies: userData?.fixingCompanies ?? [],
		hasCargoRelet,
		cargos,
	});
	const handleCreateCargoRelet = () => {
		onCreateNewCargoReletMutator.mutate(undefined, {
			onSuccess: () => {
				if (selectedEstimateId == null) {
					return;
				}

				setReletViewByEstimateId((prev) => ({
					...prev,
					[selectedEstimateId]: true,
				}));
			},
		});
	};

	return (
		<>
			<Card
				styles={{ body: { padding: 0 } }}
				size="small"
				extra={
					<Space size="small">
						<Button
							disabled={!showReletCargoButton || ((estimate && estimate?.cargos.length !== 1) ?? true)}
							size="small"
							icon={(
								<FontAwesomeIcon
									className={styles.editIcon}
									icon={faCalculator}
								/>
							)}
							onClick={() => setEditingCargoIntakeId(estimate?.cargos[0].id!)}
							data-testid="intakeCalculator"
						>
							Intake Calculator
						</Button>
						<Button
							onClick={() => setShowModal(true)}
							size="small"
							disabled={isFixed || !showReletCargoButton}
						>
							Import from cargo-book
						</Button>
						<Button
							size="small"
							icon={<FontAwesomeIcon icon={faRightLeft} />}
							disabled={isReletCargoButtonDisabled}
							disabledTooltip="Cannot add cargo to a fixed estimate"
							type="primary"
							onClick={handleCreateCargoRelet}
						>
							Relet cargo
						</Button>
						<Button
							onClick={() => onCreateNewCargoMutator.mutate()}
							disabled={isFixed || hasCargoRelet}
							disabledTooltip="Cannot add cargo to a fixed estimate"
							size="small"
							icon={(<PlusOutlined />)}
							type="primary"
						>
							New Cargo
						</Button>
					</Space>
				}
			>
				{cargos.length === 0 ? (
					<div style={{ padding: 16 }}>
						<Empty
							description="Create or import a cargo to estimate the voyage"
							image={manOnBoat}
						/>
					</div>
				) : (
					<div className={styles.cargos}>
						{sortedCargos.map((cargo) => (
							<div
								key={cargo.id}
								className={classNames(styles.cargo, {
									[styles.reletCargo]: cargo.contractSide === CargoContractSides.VC_IN,
								})}
								data-testid="estimate-cargo"
							>
								<Table
									rowKey="id"
									size="small"
									className={styles.table}
									pagination={false}
									columns={cargoCardColumns.firstLine}
									dataSource={[cargo]}
									scroll={{ x: 'max-content' }}
								/>
								<Table
									rowKey="id"
									size="small"
									className={styles.table}
									pagination={false}
									columns={cargoCardColumns.getSecondLine(cargo)}
									dataSource={[cargo]}
									scroll={{ x: 'max-content' }}
								/>
							</div>
						))}
					</div>
				)}
			</Card>
			<ImportFromCargoBookModal open={showModal} onClose={() => setShowModal(false)} />
			<CargoDetailsModal
				mode={'estimate'}
				cargoId={editingCargoId}
				setEditingCargoId={setEditingCargoId}
				visible={editingCargoId != null}
				isTceLocked={false}
				refresh={refreshEverything}
			/>
			<IntakeCalculatorModal
				visible={editingCargoIntakeId != null}
				setVisible={() => setEditingCargoIntakeId(null)}
				cargoTypes={relevantCargoTypes}
			/>
		</>
	);
};

export default CargosCard;
